import { ReviewStars, type ReviewStarValue } from '@/app/_common/review-stars'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { useSaveContactMutation } from '@/hooks/api/use-save-contact-mutation'
import { trimAndValidate } from '@/lib/utils'
import { DialogClose } from '@radix-ui/react-dialog'
import { T, useTranslate } from '@tolgee/react'
import createDecorator from 'final-form-focus'
import React from 'react'
import { Field, Form } from 'react-final-form'

type RateLimitIncreaseForm = {
  reason: string
  review?: ReviewStarValue
}

function RateLimitIncreaseDialog({
  children,
  getToastId,
}: Readonly<{ children: React.ReactNode; getToastId?: () => string | number }>) {
  const [open, setOpen] = React.useState(false)
  const { t } = useTranslate()
  const { mutate, isPending } = useSaveContactMutation({
    onSuccess: () => {
      toast.success(t('account.rateLimit.toasts.success'))
      setOpen(false)
      if (getToastId) {
        toast.dismiss(getToastId())
      }
    },
    onError: (e) => {
      console.error(e)
      toast.error(t('account.rateLimit.toasts.error'))
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogContent>
          <Form<RateLimitIncreaseForm>
            initialValues={{ reason: '' }}
            validate={validate}
            decorators={[focusOnErrors]}
            onSubmit={(initialValues) =>
              trimAndValidate({
                initialValues,
                validate,
                onValid: (values) =>
                  mutate({ contact_type: 'HELP', comment: values.reason, rating: values.review }),
              })
            }
          >
            {({ handleSubmit, invalid }) => (
              <form onSubmit={handleSubmit} className='flex flex-col gap-10'>
                <DialogTitle className='text-2xl font-light'>
                  <T keyName='account.rateLimit.dialog.title' />
                </DialogTitle>

                <Field name='reason'>
                  {({ input, meta }) => (
                    <div className='flex flex-col gap-1'>
                      <Label
                        htmlFor={input.name}
                        error={meta.submitFailed && meta.invalid}
                        variant='dialog-field'
                        className='text-base'
                      >
                        <T keyName='account.rateLimit.dialog.usageExplanation.label' />
                      </Label>
                      <Input
                        className='placeholder:italic'
                        placeholder={t('account.rateLimit.dialog.usageExplanation.placeholder')}
                        maxLength={MAX_REASON_CHARS}
                        error={meta.submitFailed && meta.invalid}
                        {...input}
                      />
                    </div>
                  )}
                </Field>

                <div className='flex flex-col gap-4'>
                  <Field name='review'>
                    {({ input, meta }) => (
                      <div className='flex items-center gap-4'>
                        <Label
                          htmlFor={input.name}
                          error={meta.submitFailed && meta.invalid}
                          variant='dialog-field'
                        >
                          <T keyName='account.rateLimit.dialog.review.label' />
                        </Label>
                        <ReviewStars
                          onChange={input.onChange}
                          value={input.value}
                          disabled={isPending}
                          selectedValue={input.value}
                        />
                      </div>
                    )}
                  </Field>

                  <DialogDescription className='text-xs font-light'>
                    <T keyName='account.rateLimit.dialog.footerMessage' />
                  </DialogDescription>

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button
                        variant='mango-outline'
                        disabled={isPending}
                        title={t('shared.cancelDialog.a11y')}
                      >
                        <T keyName='shared.cancelDialog.title' />
                      </Button>
                    </DialogClose>
                    <Button
                      type='submit'
                      loading={isPending}
                      disabled={invalid}
                      title={t('account.rateLimit.dialog.requestButton.a11y')}
                    >
                      <T keyName='account.rateLimit.dialog.requestButton.title' />
                    </Button>
                  </DialogFooter>
                </div>
              </form>
            )}
          </Form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

// 👇 form data

const MAX_REASON_CHARS = 1000

type RateLimitIncreaseFormErrors = Partial<Record<keyof RateLimitIncreaseForm, boolean>>

const focusOnErrors = createDecorator<RateLimitIncreaseForm>()
const validate = ({ reason }: RateLimitIncreaseForm): RateLimitIncreaseFormErrors => {
  const errors: RateLimitIncreaseFormErrors = {}

  if (!reason?.trim() || reason.length === 0) errors.reason = true

  return errors
}

export { RateLimitIncreaseDialog, type RateLimitIncreaseForm }
